import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InventoryItem } from '../../../_interfaces/inventory-item';
import { AlertService } from '../../../_services/alert.service';
import { code128Svg } from '../../../../Shared/_utils/code128';
import { InventoryService } from '../../../_services/inventory.service';
import { VariantLabel, variantLabelCss, variantLabelHtml } from './variant-label';

type Size = { key: string; label: string; w: number; h: number };
type Layout = 'roll' | 'a4';
// Regular: the small Odoo-style label. Variant: the crate label with the
// crate's and the single slab / m2 item's barcodes (see variant-label.ts).
type LabelType = 'regular' | 'variant';

// Barcode labels for the selected products, in Odoo 16's product-label
// layout: a bordered label with the SKU in bold capitals, a Code 128
// barcode and the number under it. Printed from a new window so the page's
// own layout never gets in the way.
@Component({
    selector: 'app-barcode-labels',
    imports: [CommonModule, FormsModule, MatDialogContent, MatIconModule, MatTooltipModule],
    templateUrl: './barcode-labels.component.html'
})
export class BarcodeLabelsComponent {
  readonly dialogRef = inject(MatDialogRef<BarcodeLabelsComponent>);
  private readonly items = inject<InventoryItem[]>(MAT_DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly alertService = inject(AlertService);
  private readonly inventoryService = inject(InventoryService);

  type: LabelType = 'regular';
  // Crate labels: landscape paper, one label per sheet.
  readonly variantSizes: Size[] = [
    { key: 'a5', label: 'A5 landscape', w: 210, h: 148 },
    { key: 'a4', label: 'A4 landscape', w: 297, h: 210 },
    { key: 'a6', label: 'A6 landscape', w: 148, h: 105 },
  ];
  variantSize: Size = this.variantSizes[0];
  private variantData = new Map<number, VariantLabel>();
  variantLoading = false;
  variantError = '';

  readonly sizes: Size[] = [
    { key: '50x30', label: '50 × 30 mm', w: 50, h: 30 },
    { key: '60x40', label: '60 × 40 mm', w: 60, h: 40 },
    { key: '100x50', label: '100 × 50 mm', w: 100, h: 50 },
  ];
  size: Size = this.sizes[0];
  layout: Layout = 'roll';

  // Products that have a barcode, and how many labels of each.
  readonly rows = this.items
    .filter((i) => !!i.barcode?.trim())
    .sort((a, b) => a.sku.localeCompare(b.sku))
    .map((item) => ({ item, copies: 1 }));
  readonly missing = this.items.filter((i) => !i.barcode?.trim());

  // "SKU1, SKU2, SKU3…" for the left-out note.
  get missingList(): string {
    return this.missing.slice(0, 4).map((m) => m.sku).join(', ') + (this.missing.length > 4 ? '…' : '');
  }

  get totalLabels(): number {
    return this.rows.reduce((n, r) => n + Math.max(0, Math.floor(Number(r.copies) || 0)), 0);
  }

  setAllCopies(n: number): void {
    this.rows.forEach((r) => (r.copies = n));
  }

  remove(index: number): void {
    this.rows.splice(index, 1);
  }

  setType(t: LabelType): void {
    this.type = t;
    if (t === 'variant' && !this.variantData.size && !this.variantLoading) this.loadVariantLabels();
  }

  loadVariantLabels(): void {
    this.variantLoading = true;
    this.variantError = '';
    this.inventoryService.getVariantLabels(this.rows.map((r) => r.item.productVariantId)).subscribe({
      next: (labels) => {
        this.variantData = new Map(labels.map((l) => [l.productVariantId, l]));
        this.variantLoading = false;
        this.previewCache = null;
      },
      error: (e) => {
        this.variantLoading = false;
        this.variantError = e?.error?.message || 'Could not load the crate details';
      },
    });
  }

  // Picked items with no slab / m2 item linked - their second barcode is left empty.
  get unlinkedCount(): number {
    return this.rows.filter((r) => this.variantData.has(r.item.productVariantId) && !this.variantData.get(r.item.productVariantId)!.extreme).length;
  }

  // The first product's label, drawn exactly as it will print (a crate label
  // is scaled down to fit the panel).
  private previewCache: { key: string; html: SafeHtml } | null = null;
  get preview(): SafeHtml {
    const first = this.rows[0]?.item;
    const key = `${this.type}|${this.size.key}|${this.variantSize.key}|${first?.productVariantId}|${this.variantData.size}`;
    if (this.previewCache?.key === key) return this.previewCache.html;
    let html = '';
    if (first && this.type === 'regular') {
      html = `<style>${this.labelCss()}</style>${this.labelHtml(first)}`;
    } else if (first) {
      const data = this.variantData.get(first.productVariantId);
      if (data) {
        const w = 200; // preview drawn at 200 mm wide, then scaled to the panel
        const scale = 268 / (w * 3.7795);
        html = `<style>${variantLabelCss(w)}</style><div style="width:268px;height:${Math.round(268 * 0.69)}px;overflow:hidden"><div style="transform:scale(${scale.toFixed(4)});transform-origin:top left">${variantLabelHtml(data)}</div></div>`;
      }
    }
    const safe = this.sanitizer.bypassSecurityTrustHtml(html);
    this.previewCache = { key, html: safe };
    return safe;
  }

  private escape(v: string): string {
    return v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
  }

  private labelHtml(item: InventoryItem): string {
    const code = item.barcode!.trim();
    let bars = '';
    try {
      bars = code128Svg(code);
    } catch {
      bars = '<div class="bad">Can\'t encode this barcode</div>';
    }
    return `<div class="label"><div class="sku">${this.escape(item.sku.toUpperCase())}</div><div class="bars">${bars}</div><div class="code">${this.escape(code)}</div></div>`;
  }

  // Sizes scale with the label's height, so every size keeps Odoo's look.
  private labelCss(): string {
    const { w, h } = this.size;
    return `
      .label { box-sizing: border-box; width: ${w}mm; height: ${h}mm; border: 0.25mm solid #000; background: #fff;
        padding: ${(h * 0.08).toFixed(2)}mm ${(w * 0.06).toFixed(2)}mm ${(h * 0.05).toFixed(2)}mm;
        display: flex; flex-direction: column; align-items: center; justify-content: space-between;
        font-family: 'Lato', 'Helvetica Neue', Arial, sans-serif; color: #000; overflow: hidden; }
      .label .sku { font-weight: 900; font-size: ${(h * 0.105).toFixed(2)}mm; line-height: 1.22; text-align: center;
        word-break: break-word; max-height: ${(h * 0.27).toFixed(2)}mm; overflow: hidden; letter-spacing: 0.01em; }
      .label .bars { width: 92%; height: ${(h * 0.34).toFixed(2)}mm; }
      .label .bars svg { width: 100%; height: 100%; display: block; }
      .label .code { font-size: ${(h * 0.1).toFixed(2)}mm; line-height: 1; letter-spacing: 0.02em; }
      .label .bad { font-size: 3mm; color: #b91c1c; text-align: center; }`;
  }

  print(): void {
    if (this.type === 'variant') return this.printVariant();
    const labels: string[] = [];
    for (const r of this.rows) {
      const n = Math.max(0, Math.floor(Number(r.copies) || 0));
      for (let i = 0; i < n; i++) labels.push(this.labelHtml(r.item));
    }
    if (!labels.length) {
      this.alertService.alert('Nothing to print - set at least one copy', 'error');
      return;
    }
    const { w, h } = this.size;
    const page =
      this.layout === 'roll'
        ? // One label per page, page = label - what label printers expect.
          `@page { size: ${w}mm ${h}mm; margin: 0; } .sheet { display: block; } .label { page-break-after: always; break-after: page; }`
        : // A grid on A4 with a small gap, for cutting or sticker sheets.
          `@page { size: A4; margin: 8mm; } .sheet { display: flex; flex-wrap: wrap; gap: 2mm; } .label { break-inside: avoid; }`;
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${this.escape(this.fileName)}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Lato:wght@400;900&display=swap" rel="stylesheet">
      <style>html, body { margin: 0; padding: 0; } ${this.labelCss()} ${page}</style></head>
      <body><div class="sheet">${labels.join('')}</div>
      <script>
        // Print once the font is in, so the SKU isn't drawn in a fallback.
        (document.fonts ? document.fonts.ready : Promise.resolve()).then(function () { setTimeout(function () { window.focus(); window.print(); }, 150); });
        window.onafterprint = function () { window.close(); };
      </script></body></html>`;
    this.openPrintWindow(doc);
  }

  // Crate labels: one per landscape sheet, centred.
  private printVariant(): void {
    const labels: string[] = [];
    for (const r of this.rows) {
      const data = this.variantData.get(r.item.productVariantId);
      if (!data) continue;
      const n = Math.max(0, Math.floor(Number(r.copies) || 0));
      for (let i = 0; i < n; i++) labels.push(`<div class="page">${variantLabelHtml(data)}</div>`);
    }
    if (!labels.length) {
      this.alertService.alert(this.variantLoading ? 'Still loading the crate details' : 'Nothing to print - set at least one copy', 'error');
      return;
    }
    const { w, h } = this.variantSize;
    // Leave a small margin inside the sheet; the label keeps its own shape.
    const labelW = Math.min(w - 8, ((h - 8) * 200) / 138);
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${this.escape(this.fileName)}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Lato:wght@400;600;700;900&family=Poppins:wght@400;600;700&display=swap" rel="stylesheet">
      <style>html, body { margin: 0; padding: 0; } @page { size: ${w}mm ${h}mm; margin: 0; }
        .page { width: ${w}mm; height: ${h}mm; display: flex; align-items: center; justify-content: center; page-break-after: always; break-after: page; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        ${variantLabelCss(labelW)}</style></head>
      <body>${labels.join('')}
      <script>
        (document.fonts ? document.fonts.ready : Promise.resolve()).then(function () { setTimeout(function () { window.focus(); window.print(); }, 200); });
        window.onafterprint = function () { window.close(); };
      </script></body></html>`;
    this.openPrintWindow(doc);
  }

  // Saving as PDF names the file after the page title: the SKU of the top
  // product being printed.
  private get fileName(): string {
    const top = this.rows.find((r) => Math.floor(Number(r.copies) || 0) > 0) ?? this.rows[0];
    return (top?.item.sku || 'Barcode labels').toUpperCase();
  }

  private openPrintWindow(doc: string): void {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      this.alertService.alert('Allow pop-ups for this site to print labels', 'error');
      return;
    }
    win.document.open();
    win.document.write(doc);
    win.document.close();
  }
}
